import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardMedia, IconButton, List, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TaskItem from './TaskItem';
import TaskNavigationBar from './TaskNavigationBar';
import TimerProgressCircle from './TimerComponet';
import { CompleteTaskButton } from './assets';
import oceanLaundryReward from '../assets/reward-ocean-laundry.png';
import enchantedTreeReward from '../assets/reward-enchanted-tree.png';
import cosmicGardeningReward from '../assets/reward-cosmic-gardening.png';

const WIDTH = 560;
const HEIGHT = 410;
const FRAME_MS = 250;
const SCENE_FRAMES = 32;
const STEPS = ['Put dishes in the sink', 'Wipe the counters', 'Sweep the floor'];
const SCENES = [
  { title: 'Create with AI', description: 'AI turns Clean the kitchen into three five-minute steps: put dishes in the sink, wipe the counters, and sweep the floor.' },
  { title: 'Home screen', description: 'The home screen shows Clean the kitchen, zero of three steps completed, priority five, and fifteen minutes estimated.' },
  { title: 'Focus mode', description: 'Focus on one kitchen step at a time with a countdown timer and task navigation.' },
  { title: 'Complete a task', description: 'Sweep the floor is complete. Earn ten task points and five efficiency points, doubled by a two-day streak, for thirty points.' },
  { title: 'Get a reward', description: 'A new illustrated reward is unlocked and revealed.' },
  { title: 'Reward gallery', description: 'The new reward joins your collection in the Reward Gallery.' },
];
const REWARDS = [
  { src: oceanLaundryReward, alt: 'A skier beside an ocean washing machine' },
  { src: enchantedTreeReward, alt: 'A traveler visiting an enchanted tree' },
  { src: cosmicGardeningReward, alt: 'A cosmic gardener mowing a glowing landscape' },
];
const panel = { border: '2px solid', borderColor: 'secondary.main', borderRadius: 3, p: 2, bgcolor: 'background.paper' };

const TaskSteps = ({ elapsed, all = false }) => (
  <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
    {STEPS.map((step, index) => (
      <Box component="li" key={step} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid', borderColor: 'primary.main', borderRadius: 2, opacity: all || elapsed >= 3250 + index * 500 ? 1 : 0, transition: 'opacity 200ms ease' }}>
        <Typography variant="caption">{step}</Typography>
        <Typography variant="caption" color="secondary.main">5 min</Typography>
      </Box>
    ))}
  </Stack>
);

function CreateScene({ elapsed, still }) {
  const prompt = 'Clean the kitchen';
  const ready = still || elapsed >= 3000;
  return (
    <Stack spacing={1.5}>
      <Typography variant="h5">Generate Task with AI</Typography>
      <TextField label="Task Description" value={still ? prompt : prompt.slice(0, Math.floor(elapsed / 85))} multiline rows={2} fullWidth slotProps={{ input: { readOnly: true } }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="secondary.main">Include Time Estimation</Typography>
        <Button variant="contained" size="small" endIcon={ready ? <CheckIcon /> : undefined}>{ready ? 'Task generated' : elapsed < 1800 ? 'Generate Task' : 'Generating…'}</Button>
      </Box>
      <TaskSteps elapsed={elapsed} all={still} />
    </Stack>
  );
}

function HomeScene() {
  return (
    <>
      <Typography variant="h4" sx={{ mb: 1 }}>To Do</Typography>
      <List aria-label="Example home-screen task" sx={{ p: 1, border: '2px solid', borderColor: 'secondary.main', borderRadius: 8 }}>
        <TaskItem title="Clean the kitchen" typographyList={['Completed: 0/3', { text: 'Priority: 5', userPriority: 5 }, 'Due: No due date', 'Estimated time: 15:00', '🔥 0']} duration={900} workingTime={0} type="assigned" />
      </List>
    </>
  );
}

function FocusScene({ elapsed }) {
  const index = Math.min(2, Math.floor(elapsed / 2600));
  const seconds = 300 - Math.floor((elapsed % 2600) / 1000);
  const tasks = STEPS.map((title, taskIndex) => ({ id: `arcade-step-${taskIndex}`, title, complete: taskIndex < index }));
  return (
    <Stack alignItems="center" spacing={0.75}>
      <Typography variant="caption">Clean the kitchen</Typography>
      <Typography variant="h6" color="secondary.main">{STEPS[index]}</Typography>
      <TimerProgressCircle value={seconds / 3} label={`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`} size={118} actionIcon={<PauseIcon />} />
      <CompleteTaskButton compact />
      <Box sx={{ width: '100%', '& #task-navigation-slider': { minHeight: 88, mt: 0 }, '& [data-testid="task-scroll"]': { py: 1 } }}>
        <TaskNavigationBar tasks={tasks} currentTaskIndex={index} onSelectTask={() => {}} containScroll />
      </Box>
    </Stack>
  );
}

function CompleteScene({ elapsed, still }) {
  const total = still ? 30 : Math.round(Math.min(1, Math.max(0, elapsed - 1000) / 1800) * 30);
  return (
    <Stack spacing={1.25} alignItems="center">
      <Typography variant="body2" color="green.main">✓ Sweep the floor complete</Typography>
      <Box sx={{ ...panel, width: 400, borderWidth: 4, boxShadow: (theme) => `0 0 20px ${theme.palette.secondary.main}55` }}>
        <Typography variant="h5" align="center" sx={{ mb: 1.5 }}>Points Earned</Typography>
        <Stack spacing={1} sx={{ borderTop: '4px solid', borderBottom: '4px solid', borderColor: 'green.main', py: 1.5 }}>
          {[['Task complete', 10], ['Time', 5], ['🔥 Streak', '× 2']].map(([label, value]) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="secondary.main">{label}</Typography><Typography variant="body2" color="secondary.main">{value}</Typography></Box>
          ))}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}><Typography variant="body1">Total</Typography><Typography variant="h5" color="green.main">+{total}</Typography></Box>
      </Box>
      <Typography variant="caption" color="secondary.main">Clean the kitchen · 3/3 complete</Typography>
    </Stack>
  );
}

function RewardScene({ elapsed, still }) {
  const revealed = still || elapsed >= 1500;
  return (
    <Stack alignItems="center" spacing={1.5}>
      <Typography variant="h5">New Reward Unlocked!</Typography>
      <Box className={`arcade-demo__reward${revealed ? ' arcade-demo__reward--revealed' : ''}`}>
        <Box className="arcade-demo__reward-back">?</Box>
        <img src={REWARDS[0].src} alt={REWARDS[0].alt} className="arcade-demo__reward-front" />
      </Box>
    </Stack>
  );
}

function GalleryScene() {
  return (
    <Stack spacing={2}>
      <Typography variant="h4">Reward Gallery</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5, pt: 2 }}>
        {REWARDS.map((reward, index) => (
          <Card key={reward.src} sx={{ border: '2px solid', borderColor: index === 0 ? 'green.main' : 'secondary.main', borderRadius: '8px', overflow: 'hidden', boxShadow: index === 0 ? '0 0 18px #39ff1444' : 'none' }}>
            <CardMedia component="img" image={reward.src} alt={reward.alt} sx={{ aspectRatio: '1', objectFit: 'cover' }} />
          </Card>
        ))}
      </Box>
    </Stack>
  );
}

const VIEWS = [CreateScene, HomeScene, FocusScene, CompleteScene, RewardScene, GalleryScene];

export default function ArcadeDemo({ starting, signIn = false }) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);
  const [frame, setFrame] = useState(0);
  const [promptFrame, setPromptFrame] = useState(0);
  const [scale, setScale] = useState(1);
  const viewportRef = useRef(null);
  const sceneIndex = Math.floor(frame / SCENE_FRAMES);
  const elapsed = (frame % SCENE_FRAMES) * FRAME_MS;
  const playing = !paused && !reducedMotion && !hidden && !starting;
  const View = VIEWS[sceneIndex];
  const actionPrompt = signIn ? 'PUSH BUTTON TO SIGN IN' : 'PUSH BUTTON TO SIGN UP';

  useEffect(() => { setPromptFrame(0); }, [signIn]);

  useEffect(() => {
    const measure = () => setScale(viewportRef.current.clientWidth / WIDTH);
    measure();
    if (!window.ResizeObserver) {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const visibilityChanged = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', visibilityChanged);
    return () => document.removeEventListener('visibilitychange', visibilityChanged);
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setFrame((value) => (value + 1) % (SCENE_FRAMES * SCENES.length));
      setPromptFrame((value) => (value + 1) % 16);
    }, FRAME_MS);
    return () => window.clearInterval(timer);
  }, [playing]);

  const selectScene = (index) => {
    setFrame(((index + SCENES.length) % SCENES.length) * SCENE_FRAMES + 24);
    setPaused(true);
  };

  return (
    <>
      <div ref={viewportRef} className={`arcade-demo${playing ? '' : ' arcade-demo--paused'}`} style={{ height: HEIGHT * scale }}>
        <Box className="arcade-demo__canvas" sx={{ width: WIDTH, height: HEIGHT, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <Box className="arcade-demo__toolbar">
            <Typography component="span" variant="caption" sx={{ fontSize: 10, color: 'secondary.main' }}>DEMO · {sceneIndex + 1}/6</Typography>
            <Typography component="span" variant="caption" sx={{ fontSize: 11 }}>{SCENES[sceneIndex].title}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton aria-label="Previous demo scene" onClick={() => selectScene(sceneIndex - 1)} disabled={starting} size="small"><ChevronLeftIcon /></IconButton>
              {!reducedMotion && <IconButton aria-label={paused ? 'Play demo' : 'Pause demo'} onClick={() => setPaused((value) => !value)} disabled={starting} size="small">{paused ? <PlayArrowIcon /> : <PauseIcon />}</IconButton>}
              <IconButton aria-label="Next demo scene" onClick={() => selectScene(sceneIndex + 1)} disabled={starting} size="small"><ChevronRightIcon /></IconButton>
            </Box>
          </Box>
          <div key={sceneIndex} className="arcade-demo__scene" role="img" aria-label={SCENES[sceneIndex].description}>
            {/* The app controls are a local visual demonstration; playback controls remain interactive. */}
            <div inert="" aria-hidden="true"><View elapsed={elapsed} still={reducedMotion} /></div>
          </div>
          <div className="arcade-demo__progress" aria-hidden="true">
            {SCENES.map((scene, index) => <span key={scene.title} style={{ opacity: index === sceneIndex ? 1 : 0.25 }} />)}
          </div>
          <div className="arcade-screen__prompt" id="arcade-start-prompt">
            {starting ? (signIn ? 'OPENING SIGN IN' : 'OPENING SIGN UP') : signIn || reducedMotion || promptFrame < 8 ? actionPrompt : '14 DAY FREE TRIAL'}
            {reducedMotion && !signIn && !starting && <span className="arcade-screen__trial">14 DAY FREE TRIAL</span>}
          </div>
        </Box>
      </div>
    </>
  );
}
